import { getUrlIdsFromNavigationPath } from "@faire/retailer-shared/categories/navigationPath";
import { NEW_PRODUCTS_CATEGORY_URL_ID } from "@faire/retailer-shared/categories/urlId";
import { trackRetailerOnboardingProductPageClickReturnToResultsClick } from "@faire/web-api/events/retailerOnboarding/click/productPageClickReturnToResults";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";
import { path as c1CategoryPath } from "@faire/web-api/routes/category/c1CategoryUrlId";
import productSearch from "@faire/web-api/routes/search";
import { useStrictLocalization } from "@faire/web/common/localization";
import { parseQueryParams } from "@faire/web/common/query-params/parseQueryParams";
import { useTraceComponentRender } from "@faire/web/common/tracing/useTraceComponentRender";
import { Button } from "@faire/web/slate/Button";
import { Arrow } from "@faire/web/slate/icons/foundation/Arrow";
import { Column, HorizontalLine } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { IBreadcrumbOption } from "@faire/retailer/components/Breadcrumb";
import { toUrlId } from "@faire/retailer/models/Category/IUrlId";
import { categoryPath } from "@faire/retailer/paths/category/categoryPath";
import { ProductPagePaths } from "@faire/retailer/paths/ProductPage";
import { getCategoryDisplayName } from "@faire/retailer/services/Categories";
import { useCategoryStore } from "@faire/retailer/stores/domain/CategoryStore";
import { findClosestActiveCategoriesByUrlIds } from "@faire/retailer/stores/domain/CategoryStore/findByUrlId";
import { ProductBreadcrumbs } from "@faire/retailer/views/product/__internal__/ProductHeader/__internal__/ProductBreadcrumbs";
import { generateBrandUrlForProductPage } from "@faire/retailer/views/product/utils/generateBrandUrlForProductPage";

export const ProductHeader = ({
  brand,
  product,
  hideBreadcrumbs,
}: {
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
  hideBreadcrumbs?: boolean;
}) => {
  useTraceComponentRender("ProductHeader");
  const location = useLocation();
  const backlinkQueryParam = parseQueryParams(location.search).get("backLink");
  const backlinkValue =
    typeof backlinkQueryParam === "string"
      ? decodeURIComponent(backlinkQueryParam)
      : undefined;

  if (backlinkValue) {
    return (
      <ProductReturnToResultsHeader
        backlink={backlinkValue}
        smallBottomMargin={hideBreadcrumbs}
      />
    );
  }

  if (hideBreadcrumbs) {
    return null;
  }

  const queryParams = ProductPagePaths.index.parseSearch(location.search);
  const brandParam = queryParams.refB;
  const searchParam = queryParams.refS;
  const categoriesParam = queryParams.refC;
  if (brandParam) {
    return <ProductBrandBreadcrumbs brand={brand} product={product} />;
  } else if (searchParam) {
    const breadcrumbOptions = [
      {
        label: searchParam,
        path: productSearch({ q: searchParam }),
      },
    ];
    return (
      <ProductBreadcrumbs
        product={product}
        breadcrumbOptions={breadcrumbOptions}
      />
    );
  } else if (categoriesParam) {
    return (
      <ProductCategoryBreadcrumbs
        product={product}
        navigationPathOrPathIdentifier={categoriesParam}
      />
    );
  } else {
    return <ProductBrandBreadcrumbs brand={brand} product={product} />;
  }
};

const ProductReturnToResultsHeader = ({
  backlink,
  smallBottomMargin,
}: {
  backlink: string;
  smallBottomMargin?: boolean;
}) => {
  // extract string between first two / characters in the backlink
  const sourcePage = backlink
    .split(new RegExp("[/?]"))
    .find((item) => item !== "");
  const { isMobile } = useViewport();
  return (
    <Column>
      <LeftAlignedButton
        variant="tertiary"
        linkTo={backlink}
        $smallBottomMargin={smallBottomMargin}
        onClick={() =>
          trackRetailerOnboardingProductPageClickReturnToResultsClick(
            sourcePage ?? "unknown"
          )
        }
      >
        <Arrow direction="left" />
        <Spacer width="1x" />
        <StrictLocalMsg
          defaultMessage="Back to results"
          description={{
            text: "Button that allows retailer to return to the category/search results page from the product page",
          }}
        />
      </LeftAlignedButton>
      {isMobile ? (
        <>
          <HorizontalLine />
          <Spacer height="5x" />
        </>
      ) : null}
    </Column>
  );
};

const LeftAlignedButton = styled(Button)<{ $smallBottomMargin?: boolean }>`
  margin-top: ${getSpacing("2x")};
  margin-bottom: ${(props) =>
    props.$smallBottomMargin ? getSpacing("1x") : getSpacing("2x")};
  align-self: flex-start;
  white-space: nowrap;

  ${responsiveMediaQueries.mobile} {
    margin-left: ${getSpacing("2x")};
  }
`;

const ProductBrandBreadcrumbs = ({
  brand,
  product,
}: {
  brand: IProductPageResponse.IPdpBrand;
  product: IProductPageResponse.IPdpProduct;
}) => {
  const breadcrumbsOptions = [
    {
      label: brand.name ?? "",
      path: generateBrandUrlForProductPage(
        brand.token ?? "",
        product.token ?? ""
      ),
    },
  ];
  return (
    <ProductBreadcrumbs
      product={product}
      breadcrumbOptions={breadcrumbsOptions}
    />
  );
};

const ProductCategoryBreadcrumbsBase = ({
  product,
  navigationPathOrPathIdentifier,
}: {
  product: IProductPageResponse.IPdpProduct;
  navigationPathOrPathIdentifier: string;
}) => {
  const { strictLocalize } = useStrictLocalization();
  const { categories } = useCategoryStore();

  const [firstCategory, ...subCategories] = findClosestActiveCategoriesByUrlIds(
    getUrlIdsFromNavigationPath(navigationPathOrPathIdentifier),
    categories
  );

  const categoryName = firstCategory?.name;
  const categoryUrlId = firstCategory?.url_id;

  const options: IBreadcrumbOption[] = [];
  if (categoryName && categoryUrlId) {
    options.push({
      label: getCategoryDisplayName(categoryUrlId, strictLocalize),
      path:
        categoryUrlId === NEW_PRODUCTS_CATEGORY_URL_ID
          ? undefined
          : c1CategoryPath(categoryUrlId),
    });

    if (subCategories) {
      const subCategoryUrlIds = [];
      for (const subCategory of subCategories) {
        if (
          subCategory.url_id === undefined ||
          subCategory.name === undefined
        ) {
          continue;
        }

        subCategoryUrlIds.push(toUrlId(subCategory.url_id));
        options.push({
          label: subCategory.name,
          path: categoryPath({
            urlIds: [toUrlId(categoryUrlId), ...subCategoryUrlIds],
          }),
        });
      }
    }
  }

  return <ProductBreadcrumbs product={product} breadcrumbOptions={options} />;
};
const ProductCategoryBreadcrumbs = observer(ProductCategoryBreadcrumbsBase);
